<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { IconInfoCircle } from '@tabler/icons-vue'
import { Button, useNotificationStore } from '@ui'
import InfoPopover from '@/ui-delta/InfoPopover.vue'
import { useAuthStore } from '@/stores/auth'
import { useColleagues, useCreateObjective, useCreateShare, useCreateTask } from '@/api/intelligence-actions'
import { useGenerateReport, type GenerateReportRequest } from '@/api/reports'
import { useOperationsSnapshot } from '@/api/snapshot'
import { useContinuitySnapshot } from '@/api/continuity-snapshot'
import { objectiveMeasures } from '@/lib/objective-measures'
import type { OriginRef } from '@/types/intelligence'
import { actionErrorText } from '@/lib/action-text'
import { localToday } from '@/lib/today'
import AssignTaskModal, { type AssignTaskPayload } from './modals/AssignTaskModal.vue'
import ShareInsightModal, { type SharePayload } from './modals/ShareInsightModal.vue'
import SetObjectiveModal, { type ObjectivePayload } from './modals/SetObjectiveModal.vue'

export type Takeaway = 'ASSIGN' | 'SHARE' | 'OBJECTIVE' | 'REPORT'

/**
 * The takeaways (S0 §33.2, P3 §3.3): assign a follow-up, share with a note,
 * set as objective, save a report, from the thing on screen. They read
 * Assign, Share, Save, Set as objective, in that order (Dao, 1 Oct 2026). Each is a
 * person's own act: nothing here is proposed or done by the model (S0 §35
 * rule 1). Which of the four a surface offers is its `actions`.
 *
 * A follow-up or a share starts from an opportunity, an objective or a saved
 * report. Something that is none of them, an Elfie answer, has no `origin`:
 * Assign and Share save it as its report first and start from that, so the
 * person receiving it reads what was seen (Dao, 1 Oct 2026).
 */
const props = withDefaults(
  defineProps<{
    workspaceId: string
    /** What a follow-up or share starts from; null when that is the report `report` saves. */
    origin: OriginRef | null
    /** What Save report saves; an opportunity's own report when not given. */
    report?: GenerateReportRequest
    /** What it is, named, for the forms to say what they act on. */
    context: string
    actions: Takeaway[]
    /** An opportunity's metric: an objective set from it is on that metric, one of the five or a care journey share. */
    objectiveMetricId?: string
    defaultTitle?: string
  }>(),
  { report: undefined, objectiveMetricId: undefined, defaultTitle: '' },
)
const emit = defineEmits<{ reportSaved: [reportId: string] }>()
const { t, te } = useI18n()
const notifications = useNotificationStore()
const { user } = storeToRefs(useAuthStore())

const open = ref<Takeaway | null>(null)
const colleagues = useColleagues(
  () => props.workspaceId,
  computed(() => open.value === 'ASSIGN' || open.value === 'SHARE'),
)
// It carries the workspace's time zone: due dates and target dates are days there, not on the reader's calendar.
const lastThirtyDays = useOperationsSnapshot(
  () => props.workspaceId,
  { period: 'LAST_30_DAYS' },
  computed(() => open.value === 'OBJECTIVE' || open.value === 'ASSIGN'),
)
const today = computed(() => localToday(lastThirtyDays.data.value?.period.timezone))
// The care journey's shares as they stand, for an objective on one (DECISIONS.md #45).
const careNow = useContinuitySnapshot(
  () => props.workspaceId,
  { period: 'LAST_30_DAYS' },
  computed(() => open.value === 'OBJECTIVE'),
)
const measures = computed(() => objectiveMeasures(lastThirtyDays.data.value?.metrics, careNow.data.value, careNow.isError.value))

const createTask = useCreateTask(() => props.workspaceId)
const createShare = useCreateShare(() => props.workspaceId)
const createObjective = useCreateObjective(() => props.workspaceId)
const generateReport = useGenerateReport(() => props.workspaceId)

const done = (key: string): void => {
  notifications.showNotification({ status: 'SUCCESS', text: t(key) })
  open.value = null
}
const failed = (error: unknown): void => {
  notifications.showNotification({ status: 'DANGER', text: actionErrorText(t, te, error) })
}

const savedReportId = ref<string | null>(null)
const toSave = computed<GenerateReportRequest | null>(
  () => props.report ?? (props.origin?.kind === 'OPPORTUNITY' ? { kind: 'OPPORTUNITY', opportunityId: props.origin.id } : null),
)
/** The saved report, saving it the first time. */
const ensureSaved = async (): Promise<string> => {
  if (savedReportId.value) return savedReportId.value
  if (!toSave.value) throw new Error('Nothing to save')
  const report = await generateReport.mutateAsync(toSave.value)
  savedReportId.value = report.id
  emit('reportSaved', report.id)
  return report.id
}
/** Where a follow-up or share starts from: the thing on screen, or its saved report. */
const startsFrom = computed<OriginRef | null>(
  () => props.origin ?? (savedReportId.value ? { kind: 'REPORT', id: savedReportId.value } : null),
)

const begin = async (takeaway: 'ASSIGN' | 'SHARE'): Promise<void> => {
  if (!props.origin) {
    try {
      await ensureSaved()
    } catch (error) {
      failed(error)
      return
    }
  }
  open.value = takeaway
}

const assign = async (task: AssignTaskPayload): Promise<void> => {
  if (!startsFrom.value) return
  try {
    await createTask.mutateAsync({ origin: startsFrom.value, ...task })
    done('actions.assign.done')
  } catch (error) {
    failed(error)
  }
}

const share = async (payload: SharePayload): Promise<void> => {
  if (!startsFrom.value) return
  try {
    await createShare.mutateAsync({ origin: startsFrom.value, ...payload })
    done('actions.share.done')
  } catch (error) {
    failed(error)
  }
}

const setObjective = async (objective: ObjectivePayload): Promise<void> => {
  try {
    await createObjective.mutateAsync({
      ...objective,
      ...(props.origin?.kind === 'OPPORTUNITY' ? { opportunityId: props.origin.id } : {}),
    })
    done('actions.objective.done')
  } catch (error) {
    failed(error)
  }
}

const saveReport = async (): Promise<void> => {
  try {
    await ensureSaved()
    notifications.showNotification({ status: 'SUCCESS', text: t('actions.report.done') })
  } catch (error) {
    failed(error)
  }
}
</script>

<template>
  <section class="takeaways" data-test="takeaways" :aria-label="t('actions.takeaways')">
    <span class="takeaways__label">
      {{ t('actions.takeaways') }}
      <InfoPopover
        v-if="!origin && (actions.includes('ASSIGN') || actions.includes('SHARE'))"
        :label="t('actions.answer.savesFirstLabel')"
        label-hidden
        data-test="takeaway-saves-first"
      >
        <template #icon><IconInfoCircle :size="16" aria-hidden="true" /></template>
        {{ t('actions.answer.savesFirst') }}
      </InfoPopover>
    </span>
    <div class="takeaways__buttons">
      <Button v-if="actions.includes('ASSIGN')" size="sm" data-test="takeaway-assign" @click="begin('ASSIGN')">
        {{ t('actions.assign.button') }}
      </Button>
      <Button v-if="actions.includes('SHARE')" size="sm" type="secondary" data-test="takeaway-share" @click="begin('SHARE')">
        {{ t('actions.share.button') }}
      </Button>
      <Button
        v-if="actions.includes('REPORT')"
        size="sm"
        type="secondary"
        :loading="generateReport.isPending.value"
        data-test="takeaway-report"
        @click="saveReport"
      >
        {{ t('actions.report.button') }}
      </Button>
      <Button v-if="actions.includes('OBJECTIVE')" size="sm" type="secondary" data-test="takeaway-objective" @click="open = 'OBJECTIVE'">
        {{ t('actions.objective.button') }}
      </Button>
      <RouterLink
        v-if="savedReportId"
        :to="{ name: 'workspace-report', params: { workspaceId, reportId: savedReportId } }"
        class="takeaways__link"
        data-test="takeaway-report-link"
      >
        {{ t('actions.report.view') }}
      </RouterLink>
    </div>

    <AssignTaskModal
      :open="open === 'ASSIGN'"
      :colleagues="colleagues.data.value ?? []"
      :colleagues-failed="!!colleagues.error.value"
      :context="context"
      :default-title="defaultTitle"
      :today="today"
      :pending="createTask.isPending.value"
      @close="open = null"
      @submit="assign"
    />
    <ShareInsightModal
      :open="open === 'SHARE'"
      :colleagues="colleagues.data.value ?? []"
      :colleagues-failed="!!colleagues.error.value"
      :context="context"
      :me-id="user?.id ?? ''"
      :pending="createShare.isPending.value"
      @close="open = null"
      @submit="share"
    />
    <SetObjectiveModal
      :open="open === 'OBJECTIVE'"
      :measures="measures"
      :today="today"
      :fixed-metric-id="objectiveMetricId"
      :pending="createObjective.isPending.value"
      @close="open = null"
      @submit="setObjective"
    />
  </section>
</template>

<style scoped>
.takeaways {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-sm) var(--spacing-md);
}

.takeaways__label {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  color: var(--fg-secondary);
}

/* The card says a sentence, not a heading. */
.takeaways__label :deep(.info-popover__card) {
  font-weight: var(--font-weight-regular);
  text-transform: none;
  color: var(--fg-primary);
}

.takeaways__buttons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-sm);
}

.takeaways__link {
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  font-weight: var(--font-weight-medium);
  color: var(--accent);
}
</style>
